import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: 'light' | 'dark';
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
}

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
  enableSystem?: boolean;
  /** When set, ignores localStorage and system preference — always uses this theme. */
  forcedTheme?: 'light' | 'dark';
  /** Provide context only without mutating document.documentElement classes.
   *  Use when CSS variable scoping is handled by a container class (e.g. `.theme-light`). */
  contextOnly?: boolean;
}

// Read initial theme from DOM (set by blocking script in index.html)
const getInitialResolvedTheme = (): 'light' | 'dark' => {
  if (typeof window === 'undefined') return 'light';
  return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
};

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  enableSystem = true,
  forcedTheme,
  contextOnly = false,
}: ThemeProviderProps) {
  // Context-only: provide theme value to useTheme() without touching the DOM
  if (forcedTheme && contextOnly) {
    return (
      <ThemeContext.Provider
        value={{ theme: forcedTheme, setTheme: () => {}, resolvedTheme: forcedTheme }}
      >
        {children}
      </ThemeContext.Provider>
    );
  }

  // Forced theme: skip localStorage/system preference, just apply directly
  if (forcedTheme) {
    return <ForcedThemeProvider forcedTheme={forcedTheme}>{children}</ForcedThemeProvider>;
  }

  return (
    <DynamicThemeProvider defaultTheme={defaultTheme} enableSystem={enableSystem}>
      {children}
    </DynamicThemeProvider>
  );
}

function ForcedThemeProvider({
  children,
  forcedTheme,
}: {
  children: ReactNode;
  forcedTheme: 'light' | 'dark';
}) {
  useEffect(() => {
    const root = document.documentElement;
    if (forcedTheme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
  }, [forcedTheme]);

  return (
    <ThemeContext.Provider
      value={{
        theme: forcedTheme,
        setTheme: () => {},
        resolvedTheme: forcedTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

function DynamicThemeProvider({
  children,
  defaultTheme,
  enableSystem,
}: {
  children: ReactNode;
  defaultTheme: Theme;
  enableSystem: boolean;
}) {
  const [theme, setThemeState] = useState<Theme>(defaultTheme);
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(getInitialResolvedTheme);
  const [mounted, setMounted] = useState(false);

  // Prevent hydration issues
  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted) return;

    const savedTheme = localStorage.getItem('theme') as Theme;
    if (savedTheme) {
      setThemeState(savedTheme);
    } else if (enableSystem) {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
      setResolvedTheme(systemTheme);
    }
  }, [enableSystem, mounted]);

  // Apply theme changes to DOM and localStorage
  useEffect(() => {
    if (!mounted) return;

    const root = document.documentElement;

    const getResolvedTheme = (theme: Theme): 'light' | 'dark' => {
      if (theme === 'system' && enableSystem) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }
      return theme as 'light' | 'dark';
    };

    const resolved = getResolvedTheme(theme);
    setResolvedTheme(resolved);

    if (resolved === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    localStorage.setItem('theme', theme);
  }, [theme, enableSystem, mounted]);

  useEffect(() => {
    if (!mounted || theme !== 'system' || !enableSystem) return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e: MediaQueryListEvent) => {
      setResolvedTheme(e.matches ? 'dark' : 'light');
      const root = document.documentElement;
      if (e.matches) {
        root.classList.add('dark');
      } else {
        root.classList.remove('dark');
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [theme, enableSystem, mounted]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
